import React from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import type {
  AppStackParamList,
  ModalStackParamList,
} from '../../../../navigation/AppNavigation.props';
import { logger } from '../utils';

// https://sweetgreen.atlassian.net/wiki/spaces/DES/pages/4469325825/Order+-+Reorder+Frontend+Flow
export const useReorderNavigation = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<AppStackParamList>>();
  const modalNavigation =
    useNavigation<NativeStackNavigationProp<ModalStackParamList>>();

  // We should navigate to the modal stack upon starting the reorder flow.
  const navigateToActiveBagWarning = React.useCallback(() => {
    logger.info('Navigate to ReorderActiveBagWarning');
    navigation.navigate('Modal', { screen: 'ReorderActiveBagWarning' });
  }, [navigation]);
  const navigateToConfirmLocation = React.useCallback(() => {
    logger.info('Navigate to ReorderConfirmLocation');
    navigation.navigate('Modal', { screen: 'ReorderConfirmLocation' });
  }, [navigation]);
  const navigateToChangeLocation = React.useCallback(() => {
    logger.info('Navigate to ReorderChangeLocation');
    navigation.navigate('Modal', { screen: 'ReorderChangeLocation' });
  }, [navigation]);
  const navigateToConflictReview = React.useCallback(() => {
    logger.info('Navigate to ReorderConflictReview');
    navigation.navigate('Modal', { screen: 'ReorderConflictReview' });
  }, [navigation]);

  // We should replace the current route in the modal stack upon changing steps.
  const replaceWithConfirmLocation = React.useCallback(() => {
    logger.info('Replace with ReorderConfirmLocation');
    modalNavigation.replace('ReorderConfirmLocation');
  }, [modalNavigation]);
  const replaceWithChangeLocation = React.useCallback(() => {
    logger.info('Replace with ReorderChangeLocation');
    modalNavigation.replace('ReorderChangeLocation');
  }, [modalNavigation]);
  const replaceWithConflictReview = React.useCallback(() => {
    logger.info('Replace with ReorderConflictReview');
    modalNavigation.replace('ReorderConflictReview');
  }, [modalNavigation]);

  // If the reorder modal is opened on top of the main tabs, dismissing should close the stack.
  // If the reorder modal is opened on top of another modal screen, dismissing should go back.
  const dismissReorder = React.useCallback(() => {
    logger.info('Dismiss Reorder');

    if (navigation.canGoBack()) navigation.goBack();
    else navigation.getParent()?.goBack();
  }, [navigation]);

  const finishReorder = React.useCallback(
    (restaurantSlug?: string, addressId?: string) => {
      logger.info('Reset To Bag', restaurantSlug, addressId);
      resetNavStack({ navigation, restaurantSlug, addressId, withBag: true });
    },
    [navigation],
  );

  const dismissReorderNewMenu = React.useCallback(
    (restaurantSlug?: string, addressId?: string) => {
      logger.info('Reset to Menu', restaurantSlug, addressId);
      resetNavStack({ navigation, restaurantSlug, addressId, withBag: false });
    },
    [navigation],
  );

  const dismissReorderHome = React.useCallback(() => {
    navigation.navigate('MainTabs', {
      screen: 'HomeTab',
      params: { screen: 'Home' },
    });
  }, [navigation]);

  return {
    navigateToActiveBagWarning,
    navigateToConfirmLocation,
    navigateToChangeLocation,
    navigateToConflictReview,
    replaceWithConfirmLocation,
    replaceWithChangeLocation,
    replaceWithConflictReview,
    dismissReorder,
    finishReorder,
    dismissReorderNewMenu,
    dismissReorderHome,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Resets the navigation stack to a new menu, optionally with the bag on top.
const resetNavStack = (props: ResetStackParams) => {
  const { navigation, restaurantSlug, addressId, withBag } = props;

  navigation.reset({
    routes: [
      {
        name: 'MainTabs',
        params: {
          screen: 'MenuTab',
          params: {
            screen: addressId ? 'DeliveryMenu' : 'Menu',
            params: addressId ? { addressId } : { restaurantSlug },
          },
        },
      },
      ...(withBag
        ? [
            {
              name: 'Modal' as keyof AppStackParamList,
              params: {
                screen: 'Bag',
              },
            },
          ]
        : []),
    ],
  });
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ResetStackParams = {
  navigation: NativeStackNavigationProp<AppStackParamList>;
  restaurantSlug?: string;
  addressId?: string;
  withBag?: boolean;
};
